<script setup lang="ts">
/**
 * Formulario de tarjeta de MODO DEMO. Sirve para ver y probar el flujo sin
 * llaves de MercadoPago; en vivo NUNCA se usa — ahí van los iframes del Brick
 * (ver BrickPay.vue), que es lo que mantiene el número de tarjeta fuera de
 * nuestro código.
 */
import { ref, reactive, computed } from 'vue'
import { tokenizeCard, luhnValid, detectBrand, expiryValid, type PaidPayload } from '@/payments'

const props = defineProps<{ amount: number; currency: string }>()
const emit = defineEmits<{ (e: 'paid', payload: PaidPayload): void }>()

const card = reactive({ number: '', name: '', exp: '', cvv: '', email: '' })
const busy = ref(false)
const error = ref('')

function money(n: number, c: string): string {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: c }).format(n)
}

/** Formatea el número en grupos de 4 mientras se escribe. */
function onNumber(e: Event): void {
  const digits = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 19)
  card.number = digits.replace(/(.{4})/g, '$1 ').trim()
}
/** Formatea MM/AA. */
function onExp(e: Event): void {
  const d = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4)
  card.exp = d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d
}
function onCvv(e: Event): void {
  card.cvv = (e.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 4)
}

const brand = computed(() => detectBrand(card.number))
const [expMonth, expYear] = [
  computed(() => card.exp.split('/')[0] ?? ''),
  computed(() => card.exp.split('/')[1] ?? ''),
]

const emailValid = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(card.email.trim()))

const valid = computed(
  () =>
    luhnValid(card.number) &&
    card.name.trim().length >= 3 &&
    expiryValid(expMonth.value, expYear.value) &&
    card.cvv.length >= 3 &&
    emailValid.value,
)

async function pay(): Promise<void> {
  if (!valid.value || busy.value) return
  busy.value = true
  error.value = ''
  try {
    const token = await tokenizeCard({
      number: card.number,
      name: card.name.trim(),
      expMonth: expMonth.value,
      expYear: expYear.value,
      cvv: card.cvv,
    })
    emit('paid', {
      token: token.token,
      paymentMethodId: token.paymentMethodId,
      lastFour: token.lastFour,
      installments: 1,
      email: card.email.trim(),
      name: card.name.trim(),
    })
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'No se pudo procesar la tarjeta.'
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <div class="cf">
    <div class="cf-row cf-num">
      <input
        :value="card.number" inputmode="numeric" placeholder="Número de tarjeta"
        aria-label="Número de tarjeta" @input="onNumber"
      />
      <span class="cf-brand" :class="brand">{{ brand === 'unknown' ? '💳' : brand }}</span>
    </div>
    <input
      v-model="card.name" class="cf-full" placeholder="Nombre en la tarjeta"
      aria-label="Nombre en la tarjeta" autocomplete="cc-name"
    />
    <input
      v-model="card.email" class="cf-full" type="email" inputmode="email"
      placeholder="Correo para la confirmación" aria-label="Correo electrónico" autocomplete="email"
    />
    <div class="cf-row">
      <input :value="card.exp" class="cf-half" inputmode="numeric" placeholder="MM/AA" aria-label="Vencimiento" @input="onExp" />
      <input :value="card.cvv" class="cf-half" inputmode="numeric" placeholder="CVV" aria-label="Código de seguridad" @input="onCvv" />
    </div>

    <div v-if="error" class="cf-err">{{ error }}</div>

    <button class="rm-btn wide" :disabled="!valid || busy" @click="pay">
      {{ busy ? 'Procesando…' : `Pagar ${money(props.amount, props.currency)}` }}
    </button>

    <div class="cf-demo">Modo demo · usa cualquier número válido (p. ej. 4111 1111 1111 1111)</div>
  </div>
</template>
